import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ImpactStatsCard from './components/ImpactStatsCard';
import ImpactChart from './components/ImpactChart';
import AchievementBadges from './components/AchievementBadges';
import StreakTracker from './components/StreakTracker';
import UniversityMetrics from './components/UniversityMetrics';
import GoalSetting from './components/GoalSetting';
import MethodologyExplainer from './components/MethodologyExplainer';

const ImpactTracking = () => {
  const [activeView, setActiveView] = useState('overview');
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Mock user impact data
  const impactData = {
    totalFoodRescued: 34,
    totalWaterSaved: 510,
    totalCO2Avoided: 23.8,
    treesEquivalent: 1.13,
    carMilesOffset: 58.7,
    mealsProvided: 68,
    totalSavings: 127.50
  };

  const handleShare = async () => {
    const shareData = {
      title: '¡Mi impacto ambiental con FoodRescue!',
      text: `He rescatado ${impactData?.totalFoodRescued}kg de comida, ahorrado ${impactData?.totalWaterSaved}L de agua y evitado ${impactData?.totalCO2Avoided}kg de CO₂. ¡Únete a la lucha contra el desperdicio alimentario!`,
      url: window.location?.origin
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      setShareModalOpen(true);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    alert('¡Texto copiado al portapapeles!');
    setShareModalOpen(false);
  };

  const views = [
    { id: 'overview', label: 'Resumen', icon: 'BarChart3' },
    { id: 'university', label: 'Campus', icon: 'GraduationCap' },
    { id: 'goals', label: 'Objetivos', icon: 'Target' },
    { id: 'methodology', label: 'Metodología', icon: 'Info' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Mi Impacto Ambiental - FoodRescue</title>
        <meta name="description" content="Visualiza tu impacto ambiental personal rescatando comida y reduciendo el desperdicio alimentario" />
      </Helmet>
      <div className="container mx-auto px-4 py-8 pt-20">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Mi Impacto Ambiental
            </h1>
            <p className="text-muted-foreground">
              Descubre cómo tus rescates de comida están ayudando al planeta
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Button
              variant="outline"
              onClick={handleShare}
              className="flex items-center space-x-2"
            >
              <Icon name="Share2" size={16} />
              <span>Compartir Impacto</span>
            </Button>
            
            <Button
              variant="default"
              onClick={() => window.location.href = '/consumer-dashboard'}
              className="flex items-center space-x-2"
            >
              <Icon name="ShoppingBag" size={16} />
              <span>Rescatar Más</span>
            </Button>
          </div>
        </div>

        {/* View Navigation */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1 mb-8 overflow-x-auto">
          {views?.map((view) => (
            <Button
              key={view?.id}
              variant={activeView === view?.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView(view?.id)}
              className="flex items-center space-x-2 whitespace-nowrap"
            >
              <Icon name={view?.icon} size={16} />
              <span>{view?.label}</span>
            </Button>
          ))}
        </div>

        {/* Overview View */}
        {activeView === 'overview' && (
          <div className="space-y-8">
            {/* Impact Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <ImpactStatsCard
                icon="Utensils"
                title="Comida Rescatada"
                value={impactData?.totalFoodRescued}
                unit="kg"
                equivalency={`${impactData?.mealsProvided} comidas proporcionadas`}
                color="var(--color-success)"
              />
              
              <ImpactStatsCard
                icon="Droplets"
                title="Agua Ahorrada"
                value={impactData?.totalWaterSaved}
                unit="L"
                equivalency="Equivale a 3 duchas completas"
                color="var(--color-primary)"
              />
              
              <ImpactStatsCard
                icon="Leaf"
                title="CO₂ Evitado"
                value={impactData?.totalCO2Avoided}
                unit="kg"
                equivalency={`${impactData?.treesEquivalent} árboles plantados`}
                color="var(--color-secondary)"
              />
              
              <ImpactStatsCard
                icon="Car"
                title="Millas de Auto"
                value={impactData?.carMilesOffset}
                unit="km"
                equivalency="Compensadas en emisiones"
                color="var(--color-accent)"
              />
            </div>

            {/* Chart and Achievements Row */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              <div className="xl:col-span-2">
                <ImpactChart />
              </div>
              <div>
                <StreakTracker />
              </div>
            </div>

            {/* Achievements */}
            <AchievementBadges />

            {/* Personal Savings Summary */}
            <div className="bg-gradient-to-r from-success/10 to-primary/10 rounded-lg p-6 border border-success/20">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center">
                  <Icon name="PiggyBank" size={24} color="var(--color-success)" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg text-foreground">
                    Resumen de Ahorros
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Tu impacto económico y ambiental combinado
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-success mb-1">
                    €{impactData?.totalSavings}
                  </div>
                  <div className="text-sm text-muted-foreground">Ahorrado en comida</div>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">
                    {impactData?.totalWaterSaved}L
                  </div>
                  <div className="text-sm text-muted-foreground">Agua conservada</div>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary mb-1">
                    {impactData?.totalCO2Avoided}kg
                  </div>
                  <div className="text-sm text-muted-foreground">CO₂ no emitido</div>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent mb-1">
                    {impactData?.mealsProvided}
                  </div>
                  <div className="text-sm text-muted-foreground">Comidas rescatadas</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* University View */}
        {activeView === 'university' && (
          <UniversityMetrics />
        )}

        {/* Goals View */}
        {activeView === 'goals' && (
          <GoalSetting />
        )}

        {/* Methodology View */}
        {activeView === 'methodology' && (
          <MethodologyExplainer />
        )}

        {/* Share Modal */}
        {shareModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-background rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-lg text-foreground">
                  Compartir Mi Impacto
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShareModalOpen(false)}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-foreground mb-3">
                    ¡He rescatado {impactData?.totalFoodRescued}kg de comida, ahorrado {impactData?.totalWaterSaved}L de agua 
                    y evitado {impactData?.totalCO2Avoided}kg de CO₂ con FoodRescue! 🌱
                    
                    Únete a mí en la lucha contra el desperdicio alimentario: {window.location?.origin}
                  </p>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`¡He rescatado ${impactData?.totalFoodRescued}kg de comida, ahorrado ${impactData?.totalWaterSaved}L de agua y evitado ${impactData?.totalCO2Avoided}kg de CO₂ con FoodRescue! 🌱\n\nÚnete a mí en la lucha contra el desperdicio alimentario: ${window.location?.origin}`)}
                    className="w-full"
                  >
                    <Icon name="Copy" size={16} className="mr-2" />
                    Copiar Texto
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`¡He rescatado ${impactData?.totalFoodRescued}kg de comida con FoodRescue! 🌱 Únete a la lucha contra el desperdicio alimentario`)}&url=${encodeURIComponent(window.location?.origin)}`, '_blank')}
                  >
                    <Icon name="Twitter" size={16} className="mr-2" />
                    Twitter
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location?.origin)}`, '_blank')}
                  >
                    <Icon name="Facebook" size={16} className="mr-2" />
                    Facebook
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImpactTracking;